import { createContext, useContext, useEffect, useState, ReactNode } from "react";
import { supabase } from "@/integrations/supabase/client";
import { trackEvent } from "@/lib/analytics";

interface User {
  id: string;
  email: string;
  name?: string;
  avatar_url?: string;
}

interface AuthCtx {
  user: User | null;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  signUp: (email: string, password: string) => Promise<void>;
  signInWithGoogle: () => Promise<void>;
  signOut: () => void;
}

const AuthContext = createContext<AuthCtx>({
  user: null,
  loading: true,
  signIn: async () => { },
  signUp: async () => { },
  signInWithGoogle: async () => { },
  signOut: () => { },
});

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    const hasHash = window.location.hash.includes("access_token=") || window.location.hash.includes("id_token=");

    async function checkSession() {
      try {
        const { data: { session }, error } = await supabase.auth.getSession();
        if (error) throw error;

        if (mounted) {
          if (session?.user) {
            setUser({
              id: session.user.id,
              email: session.user.email || "",
              name: session.user.user_metadata?.full_name || session.user.user_metadata?.name || "",
              avatar_url: session.user.user_metadata?.avatar_url || session.user.user_metadata?.picture || "",
            });
          } else {
            setUser(null);
          }
        }
      } catch (err) {
        console.error("Auth initialization error:", err);
      } finally {
        if (mounted && !hasHash) {
          setLoading(false);
        }
      }
    }

    checkSession();

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (session?.user) {
        if (mounted) {
          const u = {
            id: session.user.id,
            email: session.user.email || "",
            name: session.user.user_metadata?.full_name || session.user.user_metadata?.name || "",
            avatar_url: session.user.user_metadata?.avatar_url || session.user.user_metadata?.picture || "",
          };
          setUser(u);
          setLoading(false);

          // ── LOGIN PULSE ───────────────────────────────
          // Only send if it's a fresh manual login or the first check this session
          const hasPulsed = sessionStorage.getItem(`pulse_${u.id}`);
          if (!hasPulsed && (event === 'SIGNED_IN' || event === 'INITIAL_SESSION')) {
            sessionStorage.setItem(`pulse_${u.id}`, "true");
            fetch(`${import.meta.env.VITE_WORKER_URL}/api/admin/login-pulse`, {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({ user_email: u.email, user_id: u.id })
            }).catch(() => null); // Silent fail for telemetry
          }
        }
      } else {
        if (mounted) {
          setUser(null);
          if (event === 'INITIAL_SESSION' && !hasHash) {
            setLoading(false);
          }
        }
      }

      if (hasHash && mounted && loading) {
        setTimeout(() => {
          if (mounted && loading) {
            setLoading(false);
          }
        }, 5000);
      }
    });

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, []);

  // Dynamically inject the manifest.json ONLY after the user is authenticated.
  // This satisfies the requirement to restrict PWA download capability to logged-in users.
  useEffect(() => {
    if (user) {
      const existingManifest = document.querySelector('link[rel="manifest"]');
      if (!existingManifest) {
        const link = document.createElement("link");
        link.rel = "manifest";
        link.href = "/manifest.json";
        document.head.appendChild(link);
        console.log("Tactically injected PWA Manifest for authenticated session.");
      }
    } else {
      const existingManifest = document.querySelector('link[rel="manifest"]');
      if (existingManifest) {
        existingManifest.remove();
      }
    }
  }, [user]);

  const signIn = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) throw new Error(error.message);
    trackEvent("login", "auth", email);
  };

  const signUp = async (email: string, password: string) => {
    const { error } = await supabase.auth.signUp({ email, password });
    if (error) throw new Error(error.message);
    trackEvent("signup", "auth", email);
  };

  const signInWithGoogle = async () => {
    trackEvent("google_auth_attempt", "auth");
    localStorage.setItem("just_clicked_login", "true");
    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: window.location.origin,
        queryParams: {
          access_type: 'offline',
          prompt: 'consent',
        },
      }
    });
    if (error) {
      localStorage.removeItem("just_clicked_login");
      throw new Error(error.message);
    }
  };

  const signOut = async () => {
    trackEvent("logout", "auth");
    await supabase.auth.signOut();
  };

  return (
    <AuthContext.Provider value={{ user, loading, signIn, signUp, signInWithGoogle, signOut }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
